import { DateTime } from 'luxon';
import { RRule, rrulestr, Weekday } from 'rrule';
import {
  datetimeFromJSDate,
  getAdjustedDate,
  tzCorrectedDate,
} from './date.util';
import { formatTimeString } from './date.util';

export interface SessionRecurrenceInput {
  recurrenceType?: string;
  isRecursive?: boolean;
  scheduledDate?: Date | string | null;
  startTime?: string;
  timezone?: string;
  rrule?: string;
}

export interface SessionRecurrenceObject {
  type: string;
  every?: number;
  monthlyDay?: number;
  monthlyWeek?: number;
  monthlyWeekDay?: string;
  weeklyDays?: string[];
}

export interface SessionDisplayInput extends SessionRecurrenceInput {
  endTime?: string;
  recurrence?: SessionRecurrenceObject;
}

export interface SessionSummaryInput extends SessionDisplayInput {
  name: string;
}

function toDate(value?: Date | string | null): Date | null {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Calculates the next event date for a session.
 * - ONCE: returns session date if not ended, or null if past end time.
 * - Recurring: finds the immediate next active or upcoming occurrence after end time of any past occurrences.
 */
export function getNextSessionEventDate(
  session: SessionRecurrenceInput & { endTime?: string },
  options?: { fallbackDate?: Date | string | null; now?: Date },
): Date | null {
  const recurrenceType = (session.recurrenceType || '').toUpperCase();
  const now = options?.now ?? new Date();
  const tzid = session.timezone || 'Asia/Colombo';

  if (recurrenceType === 'ONCE' && session.scheduledDate && session.startTime) {
    const baseDate = toDate(session.scheduledDate);
    if (!baseDate) {
      return null;
    }

    const startDateTime = getAdjustedDate({
      date: baseDate,
      time: session.startTime,
      tzid,
    });

    const endDateTime = session.endTime
      ? getAdjustedDate({
          date: baseDate,
          time: session.endTime,
          tzid,
        })
      : new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);

    if (endDateTime < now) {
      return null;
    }

    return startDateTime;
  }

  if (session.rrule) {
    try {
      const rule = rrulestr(session.rrule);
      const wallNow = DateTime.fromJSDate(now).setZone(tzid);
      // Start 6 hours before current wall-clock time to catch any in-progress or recently started session
      const searchCursor = wallNow.minus({ hours: 6 });
      const cursorDate = new Date(
        Date.UTC(
          searchCursor.year,
          searchCursor.month - 1,
          searchCursor.day,
          searchCursor.hour,
          searchCursor.minute,
        ),
      );

      // Find the candidate occurrence at/after cursorDate (runs in at most 2 iterations)
      let occ = rule.after(cursorDate, true);
      while (occ) {
        const occDate = tzCorrectedDate(occ);
        const startDateTime = session.startTime
          ? getAdjustedDate({
              date: occDate,
              time: session.startTime,
              tzid,
            })
          : occDate;

        const endDateTime = session.endTime
          ? getAdjustedDate({
              date: occDate,
              time: session.endTime,
              tzid,
            })
          : new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);

        // If this occurrence has not finished yet, it is our active or next upcoming session
        if (endDateTime.getTime() >= now.getTime()) {
          return occDate;
        }

        // Advance directly to next occurrence
        occ = rule.after(occ, false);
      }

      return null;
    } catch {
      return null;
    }
  }

  return null;
}

export function parseRRule(rruleString: string): RRule | null {
  try {
    const rule = rrulestr(rruleString);
    return rule;
  } catch {
    return null;
  }
}

function getOrdinalSuffix(n: number): string {
  const remainder = n % 10;
  const teen = n % 100;
  if (teen >= 11 && teen <= 13) {
    return 'th';
  }

  switch (remainder) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function getRecurrenceLabel(type: string): string {
  const labels: Record<string, string> = {
    ONCE: 'Once',
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
  };

  return labels[type] || type;
}

function getShortRecurrenceLabel(type: string, every?: number): string {
  const normalizedType = type.toUpperCase();

  if (!every || every === 1) {
    return getRecurrenceLabel(normalizedType);
  }

  if (normalizedType === 'WEEKLY' && every === 2) {
    return 'Bi-Weekly';
  }

  if (normalizedType === 'DAILY') {
    return `Every ${every} days`;
  }

  if (normalizedType === 'WEEKLY') {
    return `Every ${every} weeks`;
  }

  if (normalizedType === 'MONTHLY') {
    return `Every ${every} months`;
  }

  return `Every ${every} ${normalizedType.toLowerCase()}`;
}

function getWeekdayLabel(day: string, format: 'short' | 'long'): string {
  const normalized = (day || '').trim().toUpperCase();

  const weekdayMap: Record<string, { short: string; long: string }> = {
    MONDAY: { short: 'Mon', long: 'Monday' },
    TUESDAY: { short: 'Tue', long: 'Tuesday' },
    WEDNESDAY: { short: 'Wed', long: 'Wednesday' },
    THURSDAY: { short: 'Thu', long: 'Thursday' },
    FRIDAY: { short: 'Fri', long: 'Friday' },
    SATURDAY: { short: 'Sat', long: 'Saturday' },
    SUNDAY: { short: 'Sun', long: 'Sunday' },
    MO: { short: 'Mon', long: 'Monday' },
    TU: { short: 'Tue', long: 'Tuesday' },
    WE: { short: 'Wed', long: 'Wednesday' },
    TH: { short: 'Thu', long: 'Thursday' },
    FR: { short: 'Fri', long: 'Friday' },
    SA: { short: 'Sat', long: 'Saturday' },
    SU: { short: 'Sun', long: 'Sunday' },
    MON: { short: 'Mon', long: 'Monday' },
    TUE: { short: 'Tue', long: 'Tuesday' },
    WED: { short: 'Wed', long: 'Wednesday' },
    THU: { short: 'Thu', long: 'Thursday' },
    FRI: { short: 'Fri', long: 'Friday' },
    SAT: { short: 'Sat', long: 'Saturday' },
    SUN: { short: 'Sun', long: 'Sunday' },
  };

  if (weekdayMap[normalized]) {
    return weekdayMap[normalized][format];
  }

  const cleaned = day?.trim();
  if (!cleaned) {
    return '';
  }

  if (format === 'short') {
    return `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1, 3).toLowerCase()}`;
  }

  return `${cleaned.charAt(0).toUpperCase()}${cleaned.slice(1).toLowerCase()}`;
}

function getRRuleWeekdayLabel(
  weekday: Weekday | number,
  format: 'short' | 'long',
): string {
  const index = typeof weekday === 'number' ? weekday : weekday.weekday;
  const labels = [
    { short: 'Mon', long: 'Monday' },
    { short: 'Tue', long: 'Tuesday' },
    { short: 'Wed', long: 'Wednesday' },
    { short: 'Thu', long: 'Thursday' },
    { short: 'Fri', long: 'Friday' },
    { short: 'Sat', long: 'Saturday' },
    { short: 'Sun', long: 'Sunday' },
  ];

  return labels[index]?.[format] || '';
}

function getRRuleSummaryLabel(rruleString: string): string {
  const rule = parseRRule(rruleString);
  if (!rule) {
    return '';
  }

  const options = rule.options;
  const interval = options.interval || 1;

  if (options.freq === RRule.WEEKLY) {
    const weekInterval = interval > 1 ? `Every ${interval} Weeks` : 'Weekly';
    const weeklyDays = (options.byweekday || [])
      .map((weekday) =>
        getRRuleWeekdayLabel(weekday as Weekday | number, 'short'),
      )
      .filter(Boolean);

    return weeklyDays.length > 0
      ? `${weekInterval} • ${weeklyDays.join(',')}`
      : weekInterval;
  }

  if (options.freq === RRule.MONTHLY) {
    const monthInterval = interval > 1 ? `Every ${interval} Months` : 'Monthly';
    const monthlyDay = options.bymonthday?.[0];
    const monthlySetPos = options.bysetpos?.[0];
    const monthlyWeekday = options.byweekday?.[0] as
      | Weekday
      | number
      | undefined;

    let monthlyPattern = '';
    if (
      monthlySetPos !== undefined &&
      monthlySetPos !== null &&
      monthlyWeekday !== undefined
    ) {
      const weeklyOrdinal =
        monthlySetPos <= 0
          ? 'last'
          : `${monthlySetPos}${getOrdinalSuffix(monthlySetPos)}`;
      monthlyPattern = `${weeklyOrdinal} ${getRRuleWeekdayLabel(monthlyWeekday, 'long')}`;
    } else if (monthlyDay !== undefined && monthlyDay !== null) {
      monthlyPattern = `${monthlyDay}${getOrdinalSuffix(monthlyDay)}`;
    }

    return monthlyPattern
      ? `${monthInterval} • ${monthlyPattern}`
      : monthInterval;
  }

  if (options.freq === RRule.DAILY) {
    return interval > 1 ? `Every ${interval} days` : 'Daily';
  }

  return getRecurrenceLabel(
    (RRule.FREQUENCIES[options.freq] || '').toUpperCase(),
  );
}

function getRecurrenceObjectSummaryLabel(
  recurrence: SessionRecurrenceObject,
): string {
  const { type, every, monthlyDay, monthlyWeek, monthlyWeekDay, weeklyDays } =
    recurrence;
  const normalizedType = (type || '').toUpperCase();

  if (normalizedType === 'WEEKLY') {
    const weekInterval = every && every > 1 ? `Every ${every} Weeks` : 'Weekly';
    const weeklyDaysLabel =
      weeklyDays && weeklyDays.length > 0
        ? weeklyDays.map((day) => getWeekdayLabel(day, 'short')).join(',')
        : '';

    return weeklyDaysLabel
      ? `${weekInterval} • ${weeklyDaysLabel}`
      : weekInterval;
  }

  if (normalizedType === 'MONTHLY') {
    const monthInterval =
      every && every > 1 ? `Every ${every} Months` : 'Monthly';
    const monthlyPattern =
      monthlyWeek !== undefined && monthlyWeek !== null && monthlyWeekDay
        ? `${monthlyWeek <= 0 ? 'last' : `${monthlyWeek}${getOrdinalSuffix(monthlyWeek)}`} ${getWeekdayLabel(monthlyWeekDay, 'long')}`
        : monthlyDay
          ? `${monthlyDay}${getOrdinalSuffix(monthlyDay)}`
          : '';

    return monthlyPattern
      ? `${monthInterval} • ${monthlyPattern}`
      : monthInterval;
  }

  return getShortRecurrenceLabel(normalizedType, every);
}

export function getSessionRecurrenceSummaryLabel(
  session: SessionDisplayInput,
): string {
  const recurrenceType = (session.recurrenceType || '').toUpperCase();
  if (recurrenceType === 'ONCE') {
    return (
      'Once' +
      (session.scheduledDate
        ? ` • ${new Date(session.scheduledDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}`
        : '')
    );
  }

  if (session.rrule) {
    const rruleSummary = getRRuleSummaryLabel(session.rrule);
    if (rruleSummary) {
      return rruleSummary;
    }
  }

  if (session.recurrence) {
    return getRecurrenceObjectSummaryLabel(session.recurrence);
  }

  return getRecurrenceLabel(recurrenceType);
}

export function getSessionTimeRangeLabel(
  session: SessionDisplayInput,
  options?: { fallbackDate?: Date | null; now?: Date },
): string {
  const eventDate =
    getNextSessionEventDate(session, options) ?? options?.fallbackDate;
  if (!eventDate || !session.startTime || !session.endTime) {
    return '—';
  }

  const startDateTime = getAdjustedDate({
    date: eventDate,
    time: session.startTime,
    tzid: session.timezone || 'Asia/Colombo',
  });

  const endDateTime = getAdjustedDate({
    date: eventDate,
    time: session.endTime,
    tzid: session.timezone || 'Asia/Colombo',
  });

  return `${startDateTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })} – ${endDateTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

export function getSessionNextEventDateLabel(
  session: SessionDisplayInput,
  options?: { fallbackDate?: Date | string | null; now?: Date },
): string {
  const date = getNextSessionEventDate(session, options);
  if (!date) {
    return '—';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getRelativeDateLabel(
  date: Date,
  options?: { now?: Date },
): string {
  const now = options?.now ?? new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  const startOfTarget = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const dayDiff = Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / 86400000,
  );

  if (dayDiff === 0) {
    return 'Today';
  }

  if (dayDiff === 1) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function getNextUpcomingSession<T extends SessionRecurrenceInput>(
  sessions: T[],
  options?: { fallbackDate?: Date | string | null; now?: Date },
): { session: T; date: Date } | null {
  if (sessions.length === 0) {
    return null;
  }

  const now = options?.now ?? new Date();
  const upcoming = sessions
    .map((session) => ({
      session,
      date: getNextSessionEventDate(session, {
        fallbackDate: options?.fallbackDate,
        now,
      }),
    }))
    .filter(
      (item): item is { session: T; date: Date } =>
        !!item.date && item.date >= now,
    )
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  return upcoming[0] ?? null;
}

export function getNextSessionSummaryLabel(
  sessions: SessionSummaryInput[],
  options?: { fallbackDate?: Date | string | null; now?: Date },
): string {
  const next = getNextUpcomingSession(sessions, options);
  if (!next) {
    return 'No upcoming sessions';
  }

  const dateText = getRelativeDateLabel(next.date, { now: options?.now });
  const timeText = formatTimeString(next.session.startTime || '') || '—';

  return `${dateText} @ ${timeText} (${next.session.name})`;
}

function getRRuleOccurrenceIndex(rule: RRule, occurrence: Date): number {
  let index = 0;

  rule.all((date) => {
    index += 1;
    const tzdate = tzCorrectedDate(date);
    return tzdate < occurrence;
  });

  return index;
}

function getRRuleOccurrenceTotal(rule: RRule): number | null {
  if (rule.options.count && rule.options.count > 0) {
    return rule.options.count;
  }

  if (rule.options.until) {
    return rule.all().length;
  }

  return null;
}

/**
 * Returns position text for the upcoming occurrence of a session.
 * Examples: "1/1", "3/12", "#5" (when total cannot be determined).
 */
export function getSessionEventProgressLabel(
  session: SessionDisplayInput,
  options?: { fallbackDate?: Date | string | null; now?: Date },
): string {
  const recurrenceType = (session.recurrenceType || '').toUpperCase();

  if (recurrenceType === 'ONCE') {
    return '1/1';
  }

  if (!session.rrule) {
    return '';
  }

  const rule = parseRRule(session.rrule);
  if (!rule) {
    return '';
  }

  const now = options?.now ?? new Date();
  const nextOccurrence = rule.after(datetimeFromJSDate(now), true);
  if (!nextOccurrence) {
    return '';
  }

  const currentPosition = getRRuleOccurrenceIndex(
    rule,
    tzCorrectedDate(nextOccurrence),
  );
  const totalEvents = getRRuleOccurrenceTotal(rule);

  return totalEvents
    ? `${currentPosition}/${totalEvents}`
    : `#${currentPosition}`;
}

export interface SessionOccurrenceItem {
  meetingId?: string;
  name: string;
  date: Date;
  startTime?: string;
  endTime?: string;
  price?: number;
  label: string;
}

export function getUpcomingSessionOccurrences(
  sessions: (SessionSummaryInput & {
    id?: string;
    _id?: string;
    price?: number;
  })[],
  options?: { now?: Date; limit?: number },
): SessionOccurrenceItem[] {
  const now = options?.now ?? new Date();
  const limit = options?.limit ?? 15;
  const occurrences: SessionOccurrenceItem[] = [];

  for (const session of sessions) {
    const meetingId = session.id || session._id;
    const recurrenceType = (session.recurrenceType || '').toUpperCase();
    const price = session.price;

    if (
      recurrenceType === 'ONCE' &&
      session.scheduledDate &&
      session.startTime
    ) {
      const baseDate = toDate(session.scheduledDate);
      if (baseDate) {
        const adjustedDate = getAdjustedDate({
          date: baseDate,
          time: session.startTime,
          tzid: session.timezone || 'Asia/Colombo',
        });
        if (adjustedDate >= now) {
          const dateLabel = adjustedDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          const timeLabel =
            formatTimeString(session.startTime) || session.startTime;
          const priceLabel =
            price != null
              ? price === 0
                ? ' • Free'
                : ` • LKR ${price.toLocaleString()}`
              : '';
          occurrences.push({
            meetingId,
            name: session.name,
            date: adjustedDate,
            startTime: session.startTime,
            endTime: session.endTime,
            price,
            label: `${dateLabel} @ ${timeLabel} (${session.name})${priceLabel}`,
          });
        }
      }
    } else if (session.rrule) {
      const rule = parseRRule(session.rrule);
      if (rule) {
        const maxWindow = new Date(now.getTime() + 180 * 86400000);
        const dates = rule.between(
          datetimeFromJSDate(now),
          datetimeFromJSDate(maxWindow),
          true,
        );
        for (const d of dates) {
          const tzDate = tzCorrectedDate(d);
          const dateLabel = tzDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
          const timeLabel =
            formatTimeString(session.startTime || '') ||
            session.startTime ||
            '';
          const priceLabel =
            price != null
              ? price === 0
                ? ' • Free'
                : ` • LKR ${price.toLocaleString()}`
              : '';
          occurrences.push({
            meetingId,
            name: session.name,
            date: tzDate,
            startTime: session.startTime,
            endTime: session.endTime,
            price,
            label: `${dateLabel} @ ${timeLabel} (${session.name})${priceLabel}`,
          });
        }
      }
    }
  }

  return occurrences
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, limit);
}

/**
 * Extracts ONLY the immediate next upcoming session for each meeting entity.
 * Useful when user wants to buy a single class pass for a specific meeting without seeing distant future dates.
 */
export function getNextSessionPerMeeting(
  sessions: (SessionSummaryInput & {
    id?: string;
    _id?: string;
    price?: number;
  })[],
  options?: { now?: Date; fallbackDate?: Date | string | null },
): SessionOccurrenceItem[] {
  const now = options?.now ?? new Date();
  const occurrences: SessionOccurrenceItem[] = [];

  for (const session of sessions) {
    const meetingId = session.id || session._id;
    const price = session.price;
    const nextDate = getNextSessionEventDate(session, {
      fallbackDate: options?.fallbackDate,
      now,
    });

    if (nextDate) {
      const dateLabel = nextDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
      const timeLabel =
        formatTimeString(session.startTime || '') || session.startTime || '';
      const priceLabel =
        price != null
          ? price === 0
            ? ' • Free'
            : ` • LKR ${price.toLocaleString()}`
          : '';

      occurrences.push({
        meetingId,
        name: session.name,
        date: nextDate,
        startTime: session.startTime,
        endTime: session.endTime,
        price,
        label: `${dateLabel} @ ${timeLabel} (${session.name})${priceLabel}`,
      });
    }
  }

  return occurrences.sort((a, b) => a.date.getTime() - b.date.getTime());
}

export interface SessionCountdown {
  status: 'LIVE' | 'STARTING_SOON' | 'UPCOMING' | 'ENDED';
  label: string;
  countdownText: string;
  diffMinutes: number;
  days: number;
  hours: number;
  minutes: number;
  isSessionDay: boolean;
  canJoin: boolean;
}

/**
 * Calculates countdown, formatted day/hour/minute string, and live/session-day status.
 */
export function getSessionCountdown(
  eventDate: Date,
  options?: {
    startTime?: string;
    endTime?: string;
    timezone?: string;
    now?: Date;
  },
): SessionCountdown {
  const now = options?.now ?? new Date();
  let startMs = eventDate.getTime();
  let startDate = eventDate;

  if (options?.startTime) {
    startDate = getAdjustedDate({
      date: eventDate,
      time: options.startTime,
      tzid: options?.timezone || 'Asia/Colombo',
    });
    startMs = startDate.getTime();
  }

  let endMs = startMs + 2 * 60 * 60 * 1000;
  if (options?.endTime) {
    const adjustedEnd = getAdjustedDate({
      date: eventDate,
      time: options.endTime,
      tzid: options?.timezone || 'Asia/Colombo',
    });
    endMs = adjustedEnd.getTime();
  }

  const nowMs = now.getTime();

  const isSessionDay =
    now.getFullYear() === startDate.getFullYear() &&
    now.getMonth() === startDate.getMonth() &&
    now.getDate() === startDate.getDate();

  if (nowMs >= startMs && nowMs <= endMs) {
    return {
      status: 'LIVE',
      label: 'Live Now',
      countdownText: 'Live Now',
      diffMinutes: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      isSessionDay: true,
      canJoin: true,
    };
  }

  if (nowMs > endMs) {
    return {
      status: 'ENDED',
      label: 'Session Ended',
      countdownText: 'Ended',
      diffMinutes: Math.floor((nowMs - endMs) / 60000),
      days: 0,
      hours: 0,
      minutes: 0,
      isSessionDay,
      canJoin: false,
    };
  }

  const diffMs = startMs - nowMs;
  const totalMinutes = Math.max(0, Math.floor(diffMs / 60000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  let countdownText = '';
  if (days > 0) {
    countdownText = `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    countdownText = `${hours}h ${minutes}m`;
  } else {
    countdownText = `${minutes}m`;
  }

  if (totalMinutes <= 15) {
    return {
      status: 'STARTING_SOON',
      label: totalMinutes === 0 ? 'Starting Now' : `Starts in ${totalMinutes}m`,
      countdownText,
      diffMinutes: totalMinutes,
      days,
      hours,
      minutes,
      isSessionDay: true,
      canJoin: true,
    };
  }

  return {
    status: 'UPCOMING',
    label: `Starts in ${countdownText}`,
    countdownText,
    diffMinutes: totalMinutes,
    days,
    hours,
    minutes,
    isSessionDay,
    canJoin: false,
  };
}

export interface MeetingPlanBreakdownItem {
  meetingId?: string;
  name: string;
  recurrenceLabel: string;
  timeRange: string;
  sessionCount: number;
  pricePerSession: number;
  subtotal: number;
  isCredit?: boolean;
}

export interface ExcludedSinglePassEvent {
  meetingId?: string;
  eventDate?: Date | string;
  sessionName?: string;
  price?: number;
}

export function getMeetingPlanBreakdown(
  meetings: (SessionSummaryInput & {
    id?: string;
    _id?: string;
    price?: number;
  })[],
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  defaultPrice = 0,
  excludedEvents?: ExcludedSinglePassEvent[],
): MeetingPlanBreakdownItem[] {
  if (!meetings || !startDate || !endDate) return [];
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end) return [];

  const items: MeetingPlanBreakdownItem[] = [];

  meetings.forEach((meeting) => {
    const meetingId = meeting.id || (meeting as any)._id;
    const pricePerSession =
      meeting.price != null ? meeting.price : defaultPrice;
    let sessionCount = 0;

    const matchingExcludedDates = (excludedEvents || [])
      .filter((ev) => {
        const evMeetingId = ev.meetingId;
        return (!evMeetingId || evMeetingId === meetingId) && ev.eventDate;
      })
      .map((ev) => new Date(ev.eventDate!).toISOString().slice(0, 10));

    const excludedDateSet = new Set(matchingExcludedDates);

    const recurrenceType = (meeting.recurrenceType || '').toUpperCase();
    if (
      recurrenceType === 'ONCE' ||
      (!meeting.isRecursive && meeting.scheduledDate)
    ) {
      const scheduled = toDate(meeting.scheduledDate);
      if (
        scheduled &&
        scheduled.getTime() >= start.getTime() &&
        scheduled.getTime() <= end.getTime()
      ) {
        sessionCount = 1;
      }
    } else if (meeting.rrule) {
      const rule = parseRRule(meeting.rrule);
      if (rule) {
        const occurrences = rule
          .between(datetimeFromJSDate(start), datetimeFromJSDate(end), true)
          .map((d) => tzCorrectedDate(d));

        sessionCount = occurrences.length;
      }
    }

    const recurrenceLabel =
      getSessionRecurrenceSummaryLabel(meeting) ||
      (recurrenceType === 'ONCE' ? 'Single Event' : 'Scheduled');
    const timeRange = getSessionTimeRangeLabel(meeting) || '';

    items.push({
      meetingId,
      name: meeting.name || 'Class Session',
      recurrenceLabel,
      timeRange,
      sessionCount,
      pricePerSession,
      subtotal: sessionCount * pricePerSession,
    });
  });

  // Append credit items for applicable excluded single passes that fall within the period
  if (excludedEvents && excludedEvents.length > 0) {
    excludedEvents.forEach((ev) => {
      if (!ev.eventDate) return;
      const evDate = toDate(ev.eventDate);
      if (!evDate) return;

      if (
        evDate.getTime() >= start.getTime() &&
        evDate.getTime() <= end.getTime()
      ) {
        const creditRate = ev.price != null ? ev.price : defaultPrice;
        items.push({
          meetingId: ev.meetingId,
          name: `Pass Credit: ${ev.sessionName || 'Pre-paid Session'}`,
          recurrenceLabel: `Paid session on ${evDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          timeRange: 'Already Paid',
          sessionCount: 1,
          pricePerSession: -creditRate,
          subtotal: -creditRate,
          isCredit: true,
        });
      }
    });
  }

  return items;
}

export type MeetingAccessType =
  | 'ONE_TIME'
  | 'MONTHLY'
  | 'SINGLE_EVENT'
  | 'INSTRUCTOR'
  | 'NONE';

export interface MeetingAccessResult {
  hasAccess: boolean;
  accessType: MeetingAccessType;
  effectiveSubscription: any | null;
  singlePassDate?: Date;
  isPending: boolean;
  pendingSubscription?: any | null;
}

/**
 * Evaluates subscription access for a meeting according to strict precedence:
 * ONE-TIME -> MONTHLY (if date in billing window) -> SINGLE_EVENT (if date/meeting matches) -> PENDING -> NONE
 */
export function getEffectiveMeetingAccess(params: {
  subscriptions?: any[] | null;
  meeting?: any | null;
  targetDate?: Date | string | null;
  now?: Date | string | null;
  isInstructor?: boolean;
  liveClassOwnerId?: string | null;
  userId?: string | null;
}): MeetingAccessResult {
  const {
    subscriptions = [],
    meeting,
    targetDate,
    now = new Date(),
    isInstructor = false,
    liveClassOwnerId,
    userId,
  } = params;

  // 0. Instructor Owner check
  if (
    isInstructor &&
    userId &&
    liveClassOwnerId &&
    userId.toString() === liveClassOwnerId.toString()
  ) {
    return {
      hasAccess: true,
      accessType: 'INSTRUCTOR',
      effectiveSubscription: null,
      isPending: false,
    };
  }

  const subList = Array.isArray(subscriptions) ? subscriptions : [];
  const nowDate = toDate(now) || new Date();
  const evalDate = toDate(targetDate) || nowDate;
  const meetingId = meeting
    ? (meeting.id || meeting._id)?.toString()
    : undefined;

  // Filter Active & Complete subscriptions
  const activeSubs = subList.filter((s) =>
    ['ACTIVE', 'COMPLETE'].includes((s.status || '').toUpperCase()),
  );

  // Pending subscriptions
  const pendingSubs = subList.filter(
    (s) => (s.status || '').toUpperCase() === 'PENDING',
  );

  // --- Precedence 1: ONE-TIME (SINGLE / ANNUAL / ONE_TIME) ---
  const oneTimeSub = activeSubs.find((s) => {
    const pt = (s.paymentType || '').toUpperCase();
    return pt === 'SINGLE' || pt === 'ANNUAL' || pt === 'ONE_TIME';
  });

  if (oneTimeSub) {
    return {
      hasAccess: true,
      accessType: 'ONE_TIME',
      effectiveSubscription: oneTimeSub,
      isPending: false,
    };
  }

  // --- Precedence 2: MONTHLY ---
  const monthlySub = activeSubs.find(
    (s) => (s.paymentType || '').toUpperCase() === 'MONTHLY',
  );

  if (monthlySub) {
    let isCoveredByMonthly = true;
    const deadline = toDate(
      monthlySub.nextPaymentDeadline || monthlySub.expiresOn,
    );
    if (deadline) {
      const deadlineEnd = new Date(deadline);
      deadlineEnd.setUTCHours(23, 59, 59, 999);
      if (evalDate.getTime() > deadlineEnd.getTime()) {
        isCoveredByMonthly = false;
      }
    }

    if (isCoveredByMonthly) {
      return {
        hasAccess: true,
        accessType: 'MONTHLY',
        effectiveSubscription: monthlySub,
        isPending: false,
      };
    }
  }

  // --- Precedence 3: SINGLE EVENT PASS ---
  if (meetingId) {
    const singlePass = activeSubs.find((s) => {
      if ((s.paymentType || '').toUpperCase() !== 'SINGLE_EVENT') return false;
      const subMeetingId = (
        s.targetEvent?.meetingId?.id ||
        s.targetEvent?.meetingId?._id ||
        s.targetEvent?.meetingId
      )?.toString();
      if (subMeetingId !== meetingId) return false;

      if (targetDate && s.targetEvent?.eventDate) {
        const passDate = toDate(s.targetEvent.eventDate);
        if (passDate) {
          const passDateStr = passDate.toISOString().slice(0, 10);
          const evalDateStr = evalDate.toISOString().slice(0, 10);
          if (passDateStr !== evalDateStr) {
            const diffHours =
              Math.abs(evalDate.getTime() - passDate.getTime()) /
              (1000 * 60 * 60);
            if (diffHours > 36) return false;
          }
        }
      }
      return true;
    });

    if (singlePass) {
      const passDate = singlePass.targetEvent?.eventDate
        ? toDate(singlePass.targetEvent.eventDate) || undefined
        : undefined;
      return {
        hasAccess: true,
        accessType: 'SINGLE_EVENT',
        effectiveSubscription: singlePass,
        singlePassDate: passDate,
        isPending: false,
      };
    }
  }

  // --- 4. Pending Check ---
  const pendingForMeeting = pendingSubs.find((s) => {
    if ((s.paymentType || '').toUpperCase() === 'SINGLE_EVENT' && meetingId) {
      const subMeetingId = (
        s.targetEvent?.meetingId?.id ||
        s.targetEvent?.meetingId?._id ||
        s.targetEvent?.meetingId
      )?.toString();
      return subMeetingId === meetingId;
    }
    return true;
  });

  return {
    hasAccess: false,
    accessType: 'NONE',
    effectiveSubscription: null,
    isPending: !!pendingForMeeting,
    pendingSubscription: pendingForMeeting || null,
  };
}

export interface ResourceAccessResult {
  hasAccess: boolean;
  accessType: 'INSTRUCTOR' | 'FULL_COURSE' | 'SINGLE_EVENT' | 'GENERAL' | 'NONE';
  effectiveSubscription?: any | null;
  isPending: boolean;
  isRestrictedBySinglePass: boolean;
}

/**
 * Evaluates subscription access for a resource (recording or study material).
 * - Full course subscriptions (ONE_TIME / ANNUAL / MONTHLY) get full access to all items.
 * - Single Event pass subscriptions get access to general items (no meetingId attached)
 *   and items specifically matching their targetEvent.meetingId.
 * - Unsubscribed/expired/pending users get no access.
 */
export function getEffectiveResourceAccess(params: {
  subscriptions?: any[] | null;
  resourceItem?: {
    meetingId?: any | null;
    sessionDate?: Date | string | null;
  } | null;
  isInstructor?: boolean;
  liveClassOwnerId?: string | null;
  userId?: string | null;
  now?: Date | string | null;
}): ResourceAccessResult {
  const {
    subscriptions = [],
    resourceItem,
    isInstructor = false,
    liveClassOwnerId,
    userId,
    now = new Date(),
  } = params;

  // 0. Instructor Owner check
  if (
    isInstructor &&
    userId &&
    liveClassOwnerId &&
    userId.toString() === liveClassOwnerId.toString()
  ) {
    return {
      hasAccess: true,
      accessType: 'INSTRUCTOR',
      effectiveSubscription: null,
      isPending: false,
      isRestrictedBySinglePass: false,
    };
  }

  const subList = Array.isArray(subscriptions) ? subscriptions : [];
  const activeSubs = subList.filter((s) =>
    ['ACTIVE', 'COMPLETE'].includes((s.status || '').toUpperCase()),
  );
  const pendingSubs = subList.filter(
    (s) => (s.status || '').toUpperCase() === 'PENDING',
  );

  const rawMeetingId = resourceItem?.meetingId;
  const itemMeetingId = (
    rawMeetingId?.id ||
    rawMeetingId?._id ||
    rawMeetingId
  )?.toString();
  const isGeneralItem = !itemMeetingId;
  const itemDate =
    toDate(resourceItem?.sessionDate) ||
    toDate((resourceItem as any)?.createdAt);

  // 1. ONE-TIME -> Full Course Access
  const oneTimeSub = activeSubs.find((s) =>
    ['SINGLE', 'ANNUAL', 'ONE_TIME'].includes(
      (s.paymentType || '').toUpperCase(),
    ),
  );
  if (oneTimeSub) {
    return {
      hasAccess: true,
      accessType: 'FULL_COURSE',
      effectiveSubscription: oneTimeSub,
      isPending: false,
      isRestrictedBySinglePass: false,
    };
  }

  // 2. MONTHLY -> Full Course Access (if covered by billing deadline)
  const monthlySub = activeSubs.find(
    (s) => (s.paymentType || '').toUpperCase() === 'MONTHLY',
  );
  if (monthlySub) {
    const deadline = toDate(monthlySub.nextPaymentDeadline || monthlySub.expiresOn);
    const deadlineEnd = deadline ? new Date(deadline).setUTCHours(23, 59, 59, 999) : null;
    const evalDate = itemDate || toDate(now) || new Date();
    if (!deadlineEnd || evalDate.getTime() <= deadlineEnd) {
      return {
        hasAccess: true,
        accessType: 'FULL_COURSE',
        effectiveSubscription: monthlySub,
        isPending: false,
        isRestrictedBySinglePass: false,
      };
    }
  }

  // 3. SINGLE_EVENT Pass Check
  const singleEventSubs = activeSubs.filter(
    (s) => (s.paymentType || '').toUpperCase() === 'SINGLE_EVENT',
  );
  if (singleEventSubs.length > 0) {
    if (isGeneralItem) {
      const validPass = singleEventSubs.find((s) => {
        const passDeadline = toDate(
          s.targetEvent?.eventDate || s.nextPaymentDeadline || s.expiresOn,
        );
        if (!passDeadline) return true;
        const deadlineEnd = new Date(passDeadline).setUTCHours(23, 59, 59, 999);
        return !itemDate || itemDate.getTime() <= deadlineEnd;
      });

      if (validPass) {
        return {
          hasAccess: true,
          accessType: 'GENERAL',
          effectiveSubscription: validPass,
          isPending: false,
          isRestrictedBySinglePass: false,
        };
      }
    }

    const matchingPass = singleEventSubs.find((s) => {
      const m = s.targetEvent?.meetingId;
      return (m?.id || m?._id || m)?.toString() === itemMeetingId;
    });

    if (matchingPass) {
      return {
        hasAccess: true,
        accessType: 'SINGLE_EVENT',
        effectiveSubscription: matchingPass,
        isPending: false,
        isRestrictedBySinglePass: false,
      };
    }

    return {
      hasAccess: false,
      accessType: 'NONE',
      effectiveSubscription: null,
      isPending: false,
      isRestrictedBySinglePass: true,
    };
  }

  return {
    hasAccess: false,
    accessType: 'NONE',
    effectiveSubscription: null,
    isPending: pendingSubs.length > 0,
    isRestrictedBySinglePass: false,
  };
}

